import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { CRMCategoryConfig, CRMSettings } from '../../types';
import { MoreActionsMenuButton, useCopyText } from '../../../primitives';

interface ContactCategoriesEditorProps {
  item?: CRMSettings | null;
  update: (changes: Partial<CRMSettings>) => void;
}

export const ContactCategoriesConfigurationEditor: React.FC<ContactCategoriesEditorProps> = ({ item, update }) => {
  const categories = item?.contact_categories || [];

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const copyText = useCopyText();

  const handleAdd = () => {
    // Generate the next available category code: e.g., 'category1', 'category2'
    // To prevent overlap if items were deleted, we find the highest current number.
    const existingNumbers = categories
      .map(c => parseInt(c.code.replace('category', ''), 10))
      .filter(n => !isNaN(n));
      
    const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;

    update({
      contact_categories: [
        ...categories,
        { code: `category${nextNumber}`, label: '' },
      ],
    });
  };

  const handleRemove = (indexToRemove: number) => {
    update({
      contact_categories: categories.filter((_, index) => index !== indexToRemove),
    });
  };

  const handleChange = (
    index: number,
    field: keyof CRMCategoryConfig,
    value: string
  ) => {
    const updatedCategories = [...categories];
    updatedCategories[index] = {
      ...updatedCategories[index],
      [field]: value,
    };
    update({ contact_categories: updatedCategories });
  };

  // Native Drag and Drop Handlers
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, dropIndex: number) => {
    e.preventDefault();

    if (draggedIndex !== null && draggedIndex !== dropIndex) {
      const updatedCategories = [...categories];
      const [draggedItem] = updatedCategories.splice(draggedIndex, 1);
      updatedCategories.splice(dropIndex, 0, draggedItem);
      
      update({ contact_categories: updatedCategories });
    }

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
      <Typography variant="caption" color="textSecondary">Категории</Typography>
      
      {categories.length === 0 ? (
        <Typography color="text.secondary" variant="body2">
          Нет добавленных категорий.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {categories.map((cat, index) => {
            const isDragging = draggedIndex === index;
            const isDragOver = dragOverIndex === index && draggedIndex !== index;

            return (
              <Paper
                key={cat.code} // Using autogenerated code is a safer key than index
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
                variant="outlined"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 2,
                  backgroundColor: '#fafafa',
                  opacity: isDragging ? 0.4 : 1,
                  boxShadow: isDragOver 
                    ? (draggedIndex! > index ? '0 -2px 0 0 #1976d2' : '0 2px 0 0 #1976d2')
                    : 'none',
                  transition: 'box-shadow 0.2s, opacity 0.2s',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'grab',
                    color: 'action.active',
                    '&:active': { cursor: 'grabbing' }
                  }}
                >
                  <DragIndicatorIcon />
                </Box>

                <TextField
                  size="small"
                  label="Название (Label)"
                  value={cat.label}
                  onChange={(e) => handleChange(index, 'label', e.target.value)}
                  placeholder="e.g. VIP Клиент"
                  fullWidth
                />

                <MoreActionsMenuButton
                  strippedButton
                  actions={[
                    ["remove", "удалить", () => handleRemove(index)],
                    ["id", `внутренний код: ${cat.code}`, () => copyText(cat.code)],
                  ]}
                  />
              </Paper>
            );
          })}
        </Box>
      )}

      <Box>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          size="small"
        >
          Добавить категорию
        </Button>
      </Box>
    </Box>
  );
};
